import { Box, Button } from "@mui/material";
import {
  createFileRoute,
  Link,
  Outlet,
  useParams,
  useSearch,
  useMatchRoute,
} from "@tanstack/react-router";

import { EditAudienceActions } from "@/features/Audiences/EditAudience/SaveAudience";
import { useGoBackToAudience } from "@/features/Audiences/return";
import { OrionBackButton } from "@/orion/OrionComponents/OrionBackButton";
import { OrionPageHeader } from "@/orion/OrionComponents/OrionPageHeader";
import theme from "@/theme";

export const Route = createFileRoute("/_auth/people/$siteId/audiences")({
  component: () => {
    return <AudienceRoute />;
  },
});

const backPaths = [
  "/people/$siteId/audiences/$audienceId/members/",
  "/people/$siteId/audiences/$audienceId/edit",
  "/people/$siteId/audiences/exports",
] as const;

const editAudienceRoutes = [
  "/people/$siteId/audiences/new",
  "/people/$siteId/audiences/$audienceId/edit",
] as const;

const AudienceRoute = () => {
  const { siteId } = useParams({ from: "/_auth/people/$siteId/audiences" });
  const matchRoute = useMatchRoute();
  const search = useSearch({ strict: false });

  const showBackButton = backPaths.some((v) =>
    matchRoute({ from: v, fuzzy: true }),
  );
  const isEditAudienceRoute = editAudienceRoutes.some((v) =>
    matchRoute({ from: v, fuzzy: true }),
  );
  // Any non-index audience page (new / edit / detail / exports): show the
  // "Audiences" crumb linking back to the listing, matching the Lists pattern.
  const isAudienceSubRoute = showBackButton || isEditAudienceRoute;
  const isExportsRoute = !!matchRoute({
    from: "/people/$siteId/audiences/exports",
  });

  const goBack = useGoBackToAudience(siteId, search.returnTo);

  return (
    <>
      <Box
        sx={{
          background: `${theme.palette.background["paper-elevation-1"]}`,
          width: "100%",
          height: "100%",
          flex: "1",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <OrionPageHeader
          paddingY={6}
          breadcrumbs={
            isAudienceSubRoute && !isExportsRoute
              ? [
                  { label: "People" },
                  {
                    label: "Audiences",
                    to: "/people/$siteId/audiences",
                    params: { siteId },
                  },
                ]
              : [{ label: "People" }]
          }
          title={isExportsRoute ? "Exports" : "Audiences"}
          description={
            isExportsRoute
              ? "Download your list and audience exports. Download links expire 24 hours after an export finishes."
              : "Audiences built using rules and conditions to define exactly who you want to reach."
          }
          actions={
            <>
              {isExportsRoute ? (
                <OrionBackButton
                  variant="outlined"
                  data-testid="exports-back-btn"
                  fallbackTo="/people/$siteId/audiences"
                  fallbackParams={{ siteId }}
                />
              ) : showBackButton ? (
                <OrionBackButton variant="outlined" onBack={goBack} />
              ) : null}
              {isExportsRoute ? null : isEditAudienceRoute ? (
                <EditAudienceActions siteId={siteId} />
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to="/people/$siteId/audiences/new"
                  params={{ siteId }}
                >
                  NEW AUDIENCE
                </Button>
              )}
            </>
          }
        />
        <Outlet />
      </Box>
    </>
  );
};
